import * as React from 'react';
import styled from 'styled-components';
import { Search16 } from 'ui/icons/aurora/16px/Search16';
import { Close16 } from 'ui/icons/action/Close16';
import { colors, spacing } from 'ui/theme';
import { noop } from 'utils/noop';
import { ExcludeProperties } from 'utils/tsHelpers';

import { StyledWrapper, StyledInput, Positioner } from './../InputStyles';

type BaseInputSearchProps = {
  /** Whether or not the input should display autocomplete */
  autoComplete?: string;
  /** optional override for data-test tag */
  'data-test'?: string;
  /** handle clearing the search bar */
  handleClear?: () => void;
  /** a unique identifier */
  id: string;
  /** if the input is non-editable */
  isReadOnly?: boolean;
  /** if the input is disabled */
  isDisabled?: boolean;
  /** if the input is required */
  isRequired?: boolean;
  /** the function to run when the input is updated */
  onChange?: (value: string) => void;
  /** The placeholder text in the input */
  placeholder?: string;
  /** The value of the input */
  value: string;
  /** the function to run when you press a certain key */
  onKeyPress?: (e: React.KeyboardEvent) => void;
  /** the width of the input */
  width?: string;
};

export type InputSearchProps = BaseInputSearchProps &
  ExcludeProperties<React.InputHTMLAttributes<HTMLInputElement>, BaseInputSearchProps>;

const Clearable = styled(StyledInput)<{ value: string }>`
  ${({ value }) => (value.length > 0 ? `padding-right: ${spacing.double};` : '')}
  position: relative;
`;
const FloatedClearButton = styled.button`
  align-items: center;
  background: transparent;
  border: none;
  display: flex;
  position: absolute;
  right: ${spacing.half};

  :focus {
    outline: none;
  }

  &&& {
    path {
      fill: ${colors.textSecondary};
    }

    :hover {
      path {
        fill: ${colors.iconInteractiveHover};
      }
    }
    :focus {
      path {
        fill: ${colors.iconInteractive};
      }
    }
  }
`;

const FocusWrapper = styled(StyledWrapper)`
  :hover {
    path {
      fill: ${colors.iconInteractiveHover};
    }
  }
  :focus-within {
    path {
      fill: ${colors.iconInteractive};
    }
  }
`;
const StyledClearIcon = styled(Close16)`
  color: ${colors.textSecondary};
`;

export const InputSearch = ({
  autoComplete,
  handleClear,
  id = '',
  onChange = () => {
    // empty
  },
  placeholder = '',
  isReadOnly = false,
  isDisabled = false,
  isRequired = false,
  value = '',
  onKeyPress = noop,
  ...props
}: InputSearchProps) => {
  const inputEl = React.useRef<HTMLInputElement>(null);

  /**
    When a user clicks into an InputText, select all the current text.
    This has the potential to error on first render if autoFocus is used,
    so optionally chain the call.

   NOTE: Storybook documentation props currently breaks when there is optional
    chaining in the same file as the props.  Until this is fixed, we
    unfortunately have to do the below :/
   */
  const handleFocus = () => inputEl.current && inputEl.current.select && inputEl.current.select();

  const isEditable = !isReadOnly && !isDisabled;

  return (
    <FocusWrapper
      data-test={props['data-test'] ? `${props['data-test']}-wrapper` : 'input-wrapper'}
      isReadOnly={!isEditable}
      width={props.width}
    >
      <Positioner color={colors.textPrimary} data-test="input-prefix" className="input-prefix">
        <Search16 data-test="search-icon" />
      </Positioner>

      <Clearable
        autoComplete={autoComplete}
        aria-required={isRequired}
        data-test="input-text"
        disabled={isDisabled}
        id={id}
        onChange={(e) => onChange(e.target.value)}
        onFocus={handleFocus}
        placeholder={placeholder}
        readOnly={isReadOnly}
        ref={inputEl}
        required={isRequired}
        tabIndex={!isEditable ? -1 : 0} // ignore tabbing when in read-only mode
        type="text" // we should use search but browsers are inconsistent with styling, this is functionally equivalent
        value={value}
        onKeyPress={onKeyPress}
        {...props}
      />

      <Positioner>
        {value.length > 0 && (
          <FloatedClearButton
            type="button"
            data-test="clear-button"
            onClick={() => {
              if (handleClear) {
                handleClear();
              } else {
                onChange(''); // if no clear handler is configured, just empty out the search string
              }
              handleFocus(); // restore the focus to the input when cleared
            }}
          >
            <StyledClearIcon data-test="clear-icon" />
          </FloatedClearButton>
        )}
      </Positioner>
    </FocusWrapper>
  );
};
